import React from 'react';
import { UserRectangle } from '@phosphor-icons/react';
import classNames from 'classnames';

import { Spinner, Tooltip } from '../../../../components/general';

import useAuthUserStore from '../../../../hooks/useAuthUserStore';

import { FocusedClip, HlReelSchemaBlock } from '../../../../types';
import { round, getThumbnail } from '../../../../utils';

interface Props {
  block: HlReelSchemaBlock | null;
  isPortrait: boolean;
  isClipSelection?: boolean;
  focusedClip?: FocusedClip | null;
  onWildcardClick?: () => void;
}

const HlReelPreviewBlock = (props: Props) => {
  const { block, isPortrait, isClipSelection, focusedClip, onWildcardClick } =
    props;

  const { adminMode } = useAuthUserStore();

  // if block is selects – get middle frame id from array, or use block frame id
  const frameId =
    (block?.type === 'selects'
      ? block?.frameIds[Math.round((block.frameIds.length - 1) / 2)]
      : block?.frameId) ?? undefined;

  const thumbnailClass = classNames(
    'rounded-md object-cover',

    !isPortrait && [
      'aspect-video',
      block?.type === 'wildcard'
        ? 'w-40 min-w-40 sm:w-48 sm:min-w-48'
        : 'w-28 min-w-28 sm:w-36 sm:min-w-36',
    ],

    isPortrait && [
      'aspect-[9/16]',
      block?.type === 'wildcard'
        ? 'w-28 min-w-28 sm:w-32 sm:min-w-32'
        : 'w-20 min-w-20 sm:w-24 sm:min-w-24',
    ],
  );

  const blockClass = classNames(
    'group relative transition-all duration-300 hover:z-10',
    { 'cursor-default hover:scale-110 hover:shadow-salmon': !isClipSelection },
    thumbnailClass,
  );

  const getImage = (alt: string) => (
    <img src={getThumbnail(frameId)} className={thumbnailClass} alt={alt} />
  );

  // PROCESSING
  if (!block)
    return (
      <div className="flex items-center justify-center">
        <Spinner color="white" className="absolute" />
        {getImage('Processing Shot Thumbnail')}
      </div>
    );

  const BlockInfoOverlay = () => (
    <div
      className={classNames(
        'absolute flex flex-col items-center justify-center p-2 bg-black-overlay-200 text-white text-center pointer-events-none',
        thumbnailClass,
      )}
    >
      <div className="capitalize font-bold">{block.type}</div>
      <div className="text-xs">{round(block.duration, adminMode)} seconds</div>
    </div>
  );

  // SELECTS

  if (block.type === 'selects')
    return (
      <div className={blockClass}>
        {!!frameId && <BlockInfoOverlay />}

        {!isClipSelection ? (
          getImage('Shot Thumbnail')
        ) : (
          <div className={classNames(thumbnailClass, 'bg-grey-200')} />
        )}
      </div>
    );

  // WILDCARD

  let isMatching = false;
  let isFaded = false;
  let wildcardTooltipText = null;

  // if user selecting where to insert a clip
  if (!!focusedClip) {
    // don't allow to select wildcard if the duration doesn't match
    if (block.duration > focusedClip.duration) {
      isFaded = true;
      wildcardTooltipText =
        "You cannot use the selected clip for this wildcard as it's not long enough";
    }

    // else – allow selecting this wildcard
    else {
      isMatching = true;
      wildcardTooltipText =
        block.clipId === focusedClip.clipId
          ? 'The selected clip is already inserted here. Click to remove it.'
          : 'Click to insert the selected clip here.';
    }
  }

  // if this is just general highlight reel preview (not clip selection)
  if (!isClipSelection)
    wildcardTooltipText =
      'The personalised attendee clip will be inserted here';

  if (block.type === 'wildcard')
    return (
      <Tooltip
        text={wildcardTooltipText}
        hide={!wildcardTooltipText}
        className={classNames(blockClass, {
          'cursor-pointer animate-glowing hover:scale-110': isMatching,
        })}
      >
        <div
          className="relative"
          onClick={isMatching ? onWildcardClick : undefined}
        >
          {/* faded overlay */}
          {isFaded && (
            <div
              className={classNames(
                'block absolute bg-grey-overlay-800 z-[1]',
                thumbnailClass,
              )}
            />
          )}

          {!block.isFilled ? (
            <div
              className={classNames(
                'flex flex-col items-center justify-center bg-grey-100 border-1 border-grey-200 border-dashed',
                thumbnailClass,
              )}
            >
              <UserRectangle
                weight="fill"
                size="2.5rem"
                className="hidden sm:block"
              />
              <div>WILDCARD</div>
              <div className="text-xs">{round(block.duration)} seconds</div>
            </div>
          ) : (
            <React.Fragment>
              <BlockInfoOverlay />
              {getImage('Wildcard Thumbnail')}
            </React.Fragment>
          )}
        </div>
      </Tooltip>
    );

  return null;
};

export default HlReelPreviewBlock;
